// src/services/EnhancedApiClient.js
import axios from "axios";
import AuthService from './AuthService';

// Mock data for development mode
const getMockStats = () => {
  return {
    totalTransactions: 1256,
    blockedTransactions: 87,
    blockRate: "6.9%",
    databaseSize: 428,
  };
};

const getMockShadowModeStatus = () => {
  return {
    enabled: true,
    lastModified: new Date().toISOString(),
  };
};

const getMockConfig = () => {
  return {
    shadowMode: true,
    environment: "Development",
    useMockData: true,
    version: "1.0.0",
  };
};

const getMockFraudAlerts = () => {
  return {
    alerts: [
      {
        uniqueId: "123456",
        timestamp: new Date().toISOString(),
        email: "test@example.com",
        ipAddress: "192.168.1.1",
        riskScore: 75,
        reasons: ["Multiple failed attempts", "Suspicious IP"],
        shadowMode: true,
      },
      // Add more mock entries as needed
    ],
    totalCount: 2,
    latestUpdate: new Date().toISOString(),
  };
};

const getMockChargebackBlocks = () => {
  return {
    blocks: [
      {
        id: "cb-001",
        timestamp: new Date().toISOString(),
        orderNumber: "SONW000436179",
        custName: "Chris Turner",
        email: "mortonlphearne3573@gmail.com",
        matchType: "email",
        billingAddress: "191 GREENTREE TRL Temple, GA 30179",
        amount: 181.84,
      }
    ],
    totalCount: 1,
    latestUpdate: new Date().toISOString(),
  };
};

const getMockCardDeclines = () => {
  return {
    declines: [
      {
        blockType: "email",
        email: "suspicious@example.com",
        ipAddress: "203.45.78.32",
        orderNumber: "ORD-12345",
        attempts: 8,
        status: "Blocked",
        isBlocked: true,
        id: "block-001",
        // Additional details for expanded view
        details: {
          firstAttempt: new Date(
            Date.now() - 1000 * 60 * 60 * 24 * 2
          ).toISOString(), // 2 days ago
          lastDecline: new Date().toISOString(),
          errorCode: "TD-A04",
          blockReason: "Multiple attempts with same email",
        },
        relatedData: {},
      },
      {
        blockType: "ip",
        email: "test@example.org",
        ipAddress: "118.25.31.89",
        orderNumber: "ORD-67890",
        attempts: 2,
        status: "Blocked",
        isBlocked: true,
        id: "block-002",
        details: {
          firstAttempt: new Date(
            Date.now() - 1000 * 60 * 60 * 12
          ).toISOString(), // 12 hours ago
          lastDecline: new Date().toISOString(),
          errorCode: "TD-A03",
          blockReason: "Multiple failed attempts from same IP",
        },
        relatedData: {},
      },
    ],
    totalBlocked: 2,
    summary: "2 active blocks and suspicious activity patterns",
    lastUpdated: new Date().toISOString(),
  };
};

// Mock data for card decline history
const getMockCardDeclineHistory = () => {
  const now = Date.now();
  return {
    declines: [
      {
        declineId: "d" + now,
        email: "customer@example.com",
        orderNumber: "ORD-" + Math.floor(Math.random() * 10000),
        timestamp: new Date().toISOString(),
        amount: 249.99,
        ipAddress: "203.45.78.32",
        declineReason: "Card declined by issuer",
        gatewayResponse: "Card has insufficient funds",
      },
      {
        declineId: "d123458",
        email: "customer@example.com",
        orderNumber: "ORD-12345",
        timestamp: new Date().toISOString(),
        amount: 249.99,
        ipAddress: "203.45.78.32",
        declineReason: "Card declined by issuer",
        gatewayResponse: "Card has insufficient funds",
      },
      {
        declineId: "d123459",
        email: "customer@example.com",
        orderNumber: "ORD-12345",
        timestamp: new Date().toISOString(),
        amount: 249.99,
        ipAddress: "203.45.78.32",
        declineReason: "Card declined by issuer",
        gatewayResponse: "Card has insufficient funds",
      },
      {
        declineId: "d123460",
        email: "customer@example.com",
        orderNumber: "ORD-12345",
        timestamp: new Date().toISOString(),
        amount: 249.99,
        ipAddress: "203.45.78.32",
        declineReason: "Card declined by issuer",
        gatewayResponse: "Card has insufficient funds",
      },
      {
        declineId: "d123461",
        email: "customer@example.com",
        orderNumber: "ORD-12345",
        timestamp: new Date().toISOString(),
        amount: 249.99,
        ipAddress: "203.45.78.32",
        declineReason: "Card declined by issuer",
        gatewayResponse: "Card has insufficient funds",
      },
    ],
    totalRecords: 5,
    timeSpan: "24 hours",
  };
};

const clearedBlocks = [];

class EnhancedApiClient {
  constructor(baseURL) {
    this.authService = AuthService;

    // Create axios instance - initialize baseURL properly later
    this.client = axios.create({
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000, // 30 second timeout for larger data sets
    });

    this.client.interceptors.request.use(
      (config) => {
        try {
          // Get API key and token from AuthService
          const apiKey = this.authService.getApiKey();
          const token = this.authService.getToken();
          // If no API key and we're authenticated, try to get it from login response
          if (!apiKey && this.authService.isAuthenticated()) {
            // Clear auth and redirect to login
            this.authService.logout();
            window.location.href = "/login";
            return Promise.reject(
              new Error("API key missing - authentication required")
            );
          }

          // Add API key if available
          if (apiKey) {
            config.headers["X-API-Key"] = apiKey;
          }

          // Add auth token if available
          if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
          }

          // Remove problematic headers
          if (config.headers["If-Modified-Since"]) {
            delete config.headers["If-Modified-Since"];
          }

          // CORS configuration
          config.mode = "cors";
          config.withCredentials = true;

          return config;
        } catch (error) {
          console.error("Error in request interceptor:", error);
          return Promise.reject(error);
        }
      },
      (error) => {
        console.error("Request interceptor error:", error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle authentication errors
        if (error.response?.status === 401) {
          console.error("Authentication error:", {
            url: error.config?.url,
            status: error.response?.status,
            message: error.response?.data?.message || error.message,
          });

          // If it's specifically an API key issue
          if (error.response?.data?.details?.reason === "missing_key") {
            console.error("API key missing or invalid - redirecting to login");
            this.authService.logout();
            window.location.href = "/login";
            return Promise.reject(
              new Error("API key invalid - authentication required")
            );
          }

          window.dispatchEvent(new CustomEvent("api:authError"));
        }

        // Handle rate limiting
        if (error.response?.status === 429) {
          console.error("Rate limit exceeded:", {
            url: error.config?.url,
            retryAfter: error.response.headers["retry-after"],
          });
        }

        // Handle CORS errors
        if (
          error.message?.includes("Network Error") ||
          error.message?.includes("CORS") ||
          error.message?.includes("cross-origin")
        ) {
          console.error("CORS error:", {
            url: error.config?.url,
            message: error.message,
            headers: error.config?.headers,
          });

          const corsError = new Error(
            `CORS error: Cannot access ${error.config?.url} from ${window.location.origin}`
          );
          corsError.originalError = error;
          corsError.isCorsError = true;
          corsError.url = error.config?.url;

          return Promise.reject(corsError);
        }

        return Promise.reject(error);
      }
    );

    // Cache configuration
    this.cacheConfig = {
      enabled: true,
      ttl: {
        stats: 5 * 60 * 1000, // 5 minutes
        fraudAlerts: 1 * 60 * 1000, // 1 minute
        cardDeclines: 1 * 60 * 1000, // 1 minute
        cardDeclineHistory: 1 * 60 * 1000, // 1 minute
        chargebackBlocks: 1 * 60 * 1000, // 1 minute
        fraudPatterns: 10 * 60 * 1000, // 10 minutes
      },
    };

    // In-memory cache
    this.cache = {};

    // Initialize the environment
    this.initializeEnvironment();
  }

  // Initialize environment settings
  initializeEnvironment() {
    // Check for environment in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const envParam = urlParams.get("env");

    // Set environment based on URL parameter
    if (envParam === "staging") {
      this.setEnvironmentConfig("staging");
    } else {
      this.setEnvironmentConfig("production");
    }
  }

  setEnvironmentConfig(env) {
    if (env === "staging") {
      // Updated to the correct Cloudflare Worker URL
      this.baseURL =
        "https://dcfraud-worker-staging.carpenter-industries-inc.workers.dev";
      this.environment = "staging";
    } else {
      // For production, update to use production worker
      this.baseURL = "https://dcfraud.dennis-carpenter.com";
      this.environment = "production";
    }

    // Update client if it exists
    if (this.client) {
      this.client.defaults.baseURL = this.baseURL;
    }
  }

  // Method to change environment
  setEnvironment(env) {
    this.setEnvironmentConfig(env);

    // Clear cache when changing environments
    this.clearCache();

    // Test connectivity after changing environment
    this.checkConnectivity().then((connected) => {
      if (connected) {
      } else {
        console.warn(
          "⚠️ Could not connect to new environment - check for CORS issues"
        );
      }
    });

    // Return the new baseURL for confirmation
    return this.baseURL;
  }

  /**
   * Set the API key
   * @param {string} apiKey - The API key
   */
  setApiKey(apiKey) {
    this.authService.setApiKey(apiKey);
    // OR if you prefer direct access:
    // localStorage.setItem(this.authService.apiKeyKey, apiKey);
  }

  /**
   * Clear the API key
   */
  clearApiKey() {
    localStorage.removeItem(this.authService.apiKeyKey);
    // OR use an AuthService method if you add one:
    // this.authService.clearApiKey();
  }

  /**
   * Get the current base URL
   * @returns {string} The current base URL
   */
  getBaseURL() {
    return this.baseURL;
  }

  /**
   * Set the base URL
   * @param {string} url - The new base URL
   */
  setBaseURL(url) {
    // NEW: Ensure URL doesn't end with a slash
    this.baseURL = url.endsWith("/") ? url.slice(0, -1) : url;
    this.client.defaults.baseURL = this.baseURL;
  }

  /**
   * Enable or disable caching
   * @param {boolean} enabled - Whether caching is enabled
   */
  setCaching(enabled) {
    this.cacheConfig.enabled = enabled;

    // Clear cache if disabling
    if (!enabled) {
      this.clearCache();
    }
  }

  /**
   * Clear the entire cache
   */
  clearCache() {
    this.cache = {};
  }

  /**
   * Clear cache for a specific endpoint
   * @param {string} endpoint - The endpoint to clear
   */
  clearCacheForEndpoint(endpoint) {
    delete this.cache[endpoint];
  }

  /**
   * Get cached response if available and not expired
   * @param {string} endpoint - The API endpoint
   * @returns {Object|null} The cached response or null
   */
  getCachedResponse(endpoint) {
    // Skip if caching disabled
    if (!this.cacheConfig.enabled) return null;

    const cachedData = this.cache[endpoint];

    // Nothing cached
    if (!cachedData) return null;

    // Get TTL for this endpoint
    const endpointKey = endpoint.split("/").pop();
    const ttl = this.cacheConfig.ttl[endpointKey] || 5 * 60 * 1000; // Default 5 min

    // Check if expired
    if (Date.now() - cachedData.timestamp > ttl) {
      return null;
    }

    return cachedData.data;
  }

  /**
   * Store response in cache
   * @param {string} endpoint - The API endpoint
   * @param {Object} data - The response data
   */
  setCachedResponse(endpoint, data) {
    // Skip if caching disabled
    if (!this.cacheConfig.enabled) return;

    this.cache[endpoint] = {
      data,
      timestamp: Date.now(),
    };
  }

  // Test KV storage functionality
  async testKvStorage() {
    try {
      return this.get("/api/admin/test-kv-storage");
    } catch (error) {
      console.error("Error testing KV storage:", error);
      throw error;
    }
  }

  /**
   * Make a request with caching
   * @param {string} method - The HTTP method
   * @param {string} endpoint - The API endpoint
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} The response data
   */
  async request(method, endpoint, options = {}) {
    const {
      data = null,
      params = null,
      skipCache = false,
      force = false,
      timeout = null,
      headers = {},
    } = options;

    // IMPROVED: Normalize endpoint to ensure it starts with a slash
    const normalizedEndpoint = endpoint.startsWith("/")
      ? endpoint
      : `/${endpoint}`;

    // Check for shadow mode endpoint - ALWAYS skip cache
    const isShadowModeEndpoint = normalizedEndpoint.includes("shadow-mode");
    const shouldSkipCache = skipCache || isShadowModeEndpoint || force;

    // Check cache first, unless skipCache is true or it's a shadow mode request
    if (!shouldSkipCache && method.toLowerCase() === "get") {
      const cachedResponse = this.getCachedResponse(normalizedEndpoint);
      if (cachedResponse) {
        console.log(`📦 Using cached response for ${normalizedEndpoint}`);
        return cachedResponse;
      }
    }

    // CONSOLIDATED: Use fetch API for all requests to ensure consistent CORS behavior
    try {
      // Build URL with parameters
      let url = normalizedEndpoint;
      if (params) {
        const queryParams = new URLSearchParams();
        for (const key in params) {
          if (params[key] !== null && params[key] !== undefined) {
            queryParams.append(key, params[key]);
          }
        }
        url = `${normalizedEndpoint}?${queryParams.toString()}`;
      }

      // Add cache buster for shadow mode requests
      if (isShadowModeEndpoint) {
        const separator = url.includes("?") ? "&" : "?";
        url = `${url}${separator}_t=${Date.now()}`;
      }

      // Ensure baseURL doesn't end with slash and URL doesn't start with double slash
      const baseUrl = this.baseURL;
      const fullUrl = `${baseUrl}${url}`;

      // Build headers
      const requestHeaders = {
        "X-API-Key": this.authService.getApiKey() || "",
        ...headers,
      };

      // Add cache-busting headers for shadow mode
      if (isShadowModeEndpoint) {
        requestHeaders["Cache-Control"] = "no-cache, no-store, must-revalidate";
        requestHeaders["Pragma"] = "no-cache";
        requestHeaders["Expires"] = "0";
        requestHeaders["X-No-Cache"] = Date.now().toString();
      }

      // Only add Content-Type for requests with data
      if (data && ["POST", "PUT", "PATCH"].includes(method.toUpperCase())) {
        requestHeaders["Content-Type"] = "application/json";
      }

      // Fetch options
      const isSameOrigin = this.baseURL.startsWith(window.location.origin);
      const fetchOptions = {
        method: method.toUpperCase(),
        headers: requestHeaders,
        mode: "cors",
        credentials: isSameOrigin ? "same-origin" : "include",
      };

      // Force no-store cache mode for shadow mode requests
      if (isShadowModeEndpoint) {
        fetchOptions.cache = "no-store";
      }

      // Add body for methods that support it
      if (data && ["POST", "PUT", "PATCH"].includes(method.toUpperCase())) {
        fetchOptions.body = JSON.stringify(data);
      }

      // Add timeout if specified
      let controller;
      if (timeout) {
        controller = new AbortController();
        fetchOptions.signal = controller.signal;
        setTimeout(() => controller.abort(), timeout);
      }

      // Make the fetch request with retry
      let response;
      let retries = 0;
      const maxRetries = isShadowModeEndpoint ? 0 : 2; // No retries for shadow mode

      while (retries <= maxRetries) {
        try {
          response = await fetch(fullUrl, fetchOptions);
          break; // Exit loop if successful
        } catch (err) {
          if (retries === maxRetries) {
            throw err; // Rethrow if out of retries
          }

          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, retries) * 1000;
          await new Promise((resolve) => setTimeout(resolve, delay));
          retries++;
        }
      }

      // Handle response based on status
      if (!response.ok) {
        // Try to get error details from response
        let errorDetails;
        try {
          errorDetails = await response.text();
        } catch (e) {
          errorDetails = "No error details available";
        }

        throw new Error(
          `Request failed with status ${response.status}: ${errorDetails}`
        );
      }

      // Parse the response
      let responseData;
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      // If this is a GET request and NOT shadow mode, cache the response
      if (method.toLowerCase() === "get" && !isShadowModeEndpoint) {
        this.setCachedResponse(normalizedEndpoint, responseData);
      }

      // Log shadow mode requests for debugging
      if (isShadowModeEndpoint) {
        console.log("🔍 Shadow mode request completed:", {
          endpoint: normalizedEndpoint,
          response: responseData,
          timestamp: new Date().toISOString(),
        });
      }

      return responseData;
    } catch (error) {
      // IMPROVED: Better CORS error detection post-fetch
      if (
        error.message &&
        (error.message.includes("has been blocked by CORS policy") ||
          error.message.includes("NetworkError") ||
          error.message.includes("Network request failed"))
      ) {
        console.error("CORS error detected in fetch:", error.message);

        // Create a more specific CORS error
        const corsError = new Error(
          `CORS error: Cannot access ${normalizedEndpoint} from this origin. Server is not allowing cross-origin requests.`
        );
        corsError.originalError = error;
        corsError.isCorsError = true;
        corsError.endpoint = normalizedEndpoint;

        throw corsError;
      }

      // Handle 304 Not Modified
      if (error.message && error.message.includes("304")) {
        return this.getCachedResponse(normalizedEndpoint);
      }

      // Fall back to mock data in development mode
      if (process.env.NODE_ENV === "development") {
        if (normalizedEndpoint.includes("stats")) {
          return getMockStats();
        } else if (normalizedEndpoint.includes("fraud-alerts")) {
          return getMockFraudAlerts();
        } else if (normalizedEndpoint.includes("card-declines")) {
          return getMockCardDeclines();
        } else if (normalizedEndpoint.includes("card-decline-history")) {
          return getMockCardDeclineHistory();
        } else if (normalizedEndpoint.includes("shadow-mode-status")) {
          // For shadow mode in dev, always return fresh mock data
          console.log("🔧 Returning mock shadow mode status (dev mode)");
          return getMockShadowModeStatus();
        } else if (normalizedEndpoint.includes("config")) {
          return getMockConfig();
        }
      }

      // For other errors, provide more details in the console
      console.error(`Error in API request to ${normalizedEndpoint}:`, error);

      // Re-throw the error for the caller to handle
      throw error;
    }
  }

  /**
   * Improved connectivity check method that avoids CORS issues
   */
  async checkConnectivity() {
    try {
      const response = await fetch(`${this.baseURL}/api/config`, {
        method: "GET",
        headers: {
          "X-API-Key": this.authService.getApiKey() || "",
        },
        mode: "cors",
        cache: "no-cache",
      });

      if (response.ok) {
        // Try to parse the response as JSON for more validation
        try {
          const data = await response.json();
          console.log("Config data received:", data);
        } catch (e) {}

        return true;
      } else {
        console.error(
          `❌ Connection failed! Status: ${response.status} ${response.statusText}`
        );
        return false;
      }
    } catch (error) {
      console.error("❌ Connection error:", error.message);

      // Report more details about CORS errors
      if (error.message && error.message.includes("CORS")) {
        console.error(
          "This appears to be a CORS policy issue. The server is not configured to allow requests from this origin."
        );
        console.error("Current origin:", window.location.origin);
        console.error("Target URL:", `${this.baseURL}/api/config`);
      }

      return false;
    }
  }

  /**
   * HTTP GET request
   * @param {string} endpoint - The API endpoint
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} The response data
   */
  async get(endpoint, options = {}) {
    const { force = false } = options;
    if (force) {
      this.clearCacheForEndpoint(endpoint);
    }
    return this.request("get", endpoint, options);
  }

  /**
   * HTTP POST request
   * @param {string} endpoint - The API endpoint
   * @param {Object} data - The request body
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} The response data
   */
  async post(endpoint, data, options = {}) {
    return this.request("post", endpoint, { ...options, data });
  }

  /**
   * Fetch dashboard statistics (REAL DATA ONLY)
   * @param {boolean} force - Whether to force fetch from API
   * @returns {Promise<Object>} The dashboard stats
   */
  async fetchStats(force = false) {
    try {
      if (force) {
        this.clearCacheForEndpoint("/api/admin/stats");
      }

      // Always make real API call regardless of environment
      const response = await this.get("/api/admin/stats", { force });

      return response;
    } catch (err) {
      console.error("❌ Error fetching real stats data:", err);
      throw new Error(`Failed to fetch statistics: ${err.message}`);
    }
  }

  /**
   * Fetch fraud alerts
   * @param {Object} options - Query options
   * @returns {Promise<Object>} The fraud alerts data
   */
  async fetchFraudAlerts(options = {}) {
    if (process.env.NODE_ENV === "development") {
      return getMockFraudAlerts();
    }

    const { limit, page, force } = options;
    const params = { limit, page };
    return this.get("/api/admin/fraud-alerts", { params, force });
  }

  /**
   * Fetch chargeback blocks
   * @param {Object} options - Query options
   * @returns {Promise<Object>} The chargeback blocks data
   */
  async fetchChargebackBlocks(options = {}) {
    if (process.env.NODE_ENV === "development") {
      return getMockChargebackBlocks();
    }

    const { limit, page, force } = options;
    const params = { limit, page };
    return this.get("/api/admin/chargeback-blocks", { params, force });
  }

  /**
   * Fetch fraud warnings (REAL DATA ONLY)
   * @param {Object} options - Query options
   * @returns {Promise<Object>} The fraud warnings data
   */
  async fetchFraudWarnings(options = {}) {
    try {
      // Force cache clearing for all fraud warning refreshes
      if (options.force) {
        this.clearCacheForEndpoint("/api/admin/fraud-warnings");
      }

      // Prepare request parameters
      const requestOptions = {
        params: {
          riskLevel: options.riskLevel,
          warningType: options.warningType,
          status: options.status,
          ...options,
        },
        skipCache: options.force === true,
        force: options.force === true,
        timestamp: options.timestamp || Date.now(),
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      };

      // Always make real API call regardless of environment
      const response = await this.get(
        "/api/admin/fraud-warnings",
        requestOptions
      );

      // Validate response structure
      if (!response || !Array.isArray(response.warnings)) {
        console.warn("⚠️ Invalid response structure from fraud warnings API");
        return { warnings: [] };
      }

      return response;
    } catch (err) {
      console.error("❌ Error fetching real fraud warnings:", err);
      throw new Error(`Failed to fetch fraud warnings: ${err.message}`);
    }
  }

  /**
   * Fetch international orders from completed orders archive
   * @param {Object} options - Query options
   * @returns {Promise<Object>} The international orders data
   */
  async fetchInternationalOrders(options = {}) {
    try {
      if (options.force) {
        this.clearCacheForEndpoint("/api/admin/international-orders");
      }

      const requestOptions = {
        skipCache: options.force === true,
        force: options.force === true,
        timestamp: options.timestamp || Date.now(),
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      };

      const response = await this.get(
        "/api/admin/international-orders",
        requestOptions
      );

      if (!response || !Array.isArray(response.orders)) {
        console.warn("⚠️ Invalid response structure from international orders API");
        return { orders: [], totalCount: 0 };
      }

      return response;
    } catch (err) {
      console.error("❌ Error fetching international orders:", err);
      throw new Error(`Failed to fetch international orders: ${err.message}`);
    }
  }

  /**
   * Review a fraud warning
   * @param {string} warningId - The warning ID
   * @param {Object} reviewData - Review data including notes
   * @returns {Promise<Object>} The review result
   */
  async reviewFraudWarning(warningId, reviewData) {
    if (process.env.NODE_ENV === "development") {
      // Simulate some processing time
      await new Promise((resolve) => setTimeout(resolve, 500));

      const result = {
        success: true,
        message: "Warning reviewed successfully",
        warningId: warningId,
        action: reviewData.action || "review",
        timestamp: new Date().toISOString(),
        status: reviewData.warningStatus || "under-review",
        reviewData: {
          notes: reviewData.notes || "No notes provided",
          reviewedBy: "admin",
          reviewedAt: new Date().toISOString(),
        },
      };
      return result;
    }

    try {
      const response = await this.post(`/api/admin/fraud-warnings/review`, {
        warningId: warningId,
        ...reviewData,
      });
      return response;
    } catch (err) {
      console.error("❌ Error reviewing fraud warning:", err);
      throw err;
    }
  }

  /**
   * Add notes to a fraud warning (changes status to under-review)
   * @param {string} warningId - The warning ID (order number like SONW000340898)
   * @param {string} notes - Notes to add
   * @returns {Promise<Object>} The result
   */
  async addNotesToFraudWarning(warningId, notes) {
    if (process.env.NODE_ENV === "development") {
      // Simulate some processing time
      await new Promise((resolve) => setTimeout(resolve, 500));

      const result = {
        success: true,
        message: "Notes added successfully - status changed to Under Review",
        warningId: warningId,
        action: "add-notes",
        timestamp: new Date().toISOString(),
        status: "under-review",
        previousStatus: "new",
        notesAdded: true,
        statusChanged: true,
        reviewData: {
          notes: notes || "No notes provided",
          reviewedBy: "admin",
          reviewedAt: new Date().toISOString(),
        },
      };

      return result;
    }

    try {
      const requestData = {
        warningId,
        notes,
        action: "add-notes",
        timestamp: new Date().toISOString(),
        reviewedBy: "admin",
      };

      const response = await this.post(
        `/api/admin/fraud-warnings/add-notes`,
        requestData
      );
      return response;
    } catch (err) {
      console.error("❌ Error adding notes to fraud warning:", err);
      throw err;
    }
  }

  /**
   * Block an order from a fraud warning
   * @param {string} warningId - The warning ID
   * @param {Object} blockData - Block reason and details
   * @returns {Promise<Object>} The block result
   */
  async blockFraudWarningOrder(warningId, blockData) {
    if (process.env.NODE_ENV === "development") {
      // Simulate some processing time
      await new Promise((resolve) => setTimeout(resolve, 750));

      return {
        success: true,
        message: "Order blocked successfully",
        warningId: warningId,
        action: "block",
        timestamp: new Date().toISOString(),
        blockData: {
          reason: blockData.reason || "Blocked due to suspicious activity",
          blockedBy: "admin",
          blockedAt: new Date().toISOString(),
          orderNumber: `ORD-${Math.floor(Math.random() * 100000)}`,
          customerNotified: true,
        },
      };
    }

    try {
      const response = await this.post(`/api/admin/fraud-warnings/block`, {
        warningId: warningId,
        ...blockData,
      });

      return response;
    } catch (err) {
      console.error("Error blocking order:", err);
      throw err;
    }
  }

  /**
   * Fetch card declines (REAL DATA ONLY)
   * @param {boolean} force - Whether to force fetch from API
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} The card declines data
   */
  async fetchCardDeclines(force = false, options = {}) {
    try {
      if (force) {
        this.clearCacheForEndpoint("/api/admin/card-declines");
      }

      // Add timestamp to request params to avoid browser/CDN caching
      const requestOptions = {
        force,
        params: {
          t: Date.now(),
          ...options,
        },
        skipCache: force,
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      };

      // Always make real API call regardless of environment
      const response = await this.get(
        "/api/admin/card-declines",
        requestOptions
      );

      // Validate response structure
      if (!response || !Array.isArray(response.declines)) {
        return { declines: [] };
      }

      return response;
    } catch (err) {
      throw new Error(`Failed to fetch card declines: ${err.message}`);
    }
  }

  /**
   * Fetch card decline history (REAL DATA ONLY)
   * @param {boolean} force - Whether to force fetch from API
   * @returns {Promise<Object>} The card decline history data
   */
  async fetchCardDeclineHistory(force = false) {
    try {
      if (force) {
        this.clearCacheForEndpoint("/api/admin/card-decline-history");
      }

      // Always make real API call regardless of environment
      const response = await this.get("/api/admin/card-decline-history", {
        force,
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      // Validate response structure
      if (!response) {
        return { history: [], count: 0 };
      }
      return response;
    } catch (err) {
      throw new Error(`Failed to fetch card decline history: ${err.message}`);
    }
  }

  /**
   * Add a fraud warning to the Google Sheets database
   * @param {string} warningId - The warning ID
   * @param {Object} additionalData - Additional data for the database entry
   * @returns {Promise<Object>} The result
   */
  async addFraudWarningToDatabase(warningId, additionalData = {}) {
    console.log("📊 ADD FRAUD WARNING TO DATABASE:", {
      warningId,
      additionalData,
    });

    if (process.env.NODE_ENV === "development") {
      // Simulate some processing time
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const result = {
        success: true,
        message: "Successfully added to fraud database",
        warningId: warningId,
        timestamp: new Date().toISOString(),
        fraudData: {
          orderNumber: warningId,
          email: additionalData.email || "test@example.com",
          billingName: additionalData.billingName || "Test User",
          reviewedBy: "admin",
        },
        databaseResult: {
          success: true,
          message: "Fraud record added to database successfully",
          details: {
            updatedRange: "FraudDatabase!A100:S100",
            updatedRows: 1,
            updatedColumns: 19,
            totalDuration: "1200ms",
          },
        },
      };
      return result;
    }

    try {
      console.log(`🚀 Adding fraud warning ${warningId} to database`);

      const requestData = {
        warningId,
        ...additionalData,
        timestamp: new Date().toISOString(),
        reviewedBy: "admin",
      };

      const response = await this.post(
        `/api/admin/fraud-warnings/add-to-database`,
        requestData
      );

      return response;
    } catch (err) {
      console.error("❌ Error adding fraud warning to database:", err);
      throw err;
    }
  }

  /**
   * Ban a fraud warning - blocks order and adds to Chargebacks_v2
   * @param {string} warningId - The warning ID
   * @param {Object} banData - Ban reason and details
   * @returns {Promise<Object>} The ban result
   */
  async banFraudWarning(warningId, banData) {
    if (process.env.NODE_ENV === "development") {
      await new Promise((resolve) => setTimeout(resolve, 750));

      return {
        success: true,
        message: "Order banned successfully",
        warningId: warningId,
        timestamp: new Date().toISOString(),
        addedToChargebacks: true,
      };
    }

    try {
      const response = await this.post(`/api/admin/fraud-warnings/ban`, {
        warningId: warningId,
        ...banData,
      });

      return response;
    } catch (err) {
      console.error("Error banning order:", err);
      throw err;
    }
  }

  /**
   * Add a Paya transaction record to the Google Sheets fraud database
   * @param {Object} payload - The transaction data to add
   * @returns {Promise<Object>} The result
   */
  async payaAddToDatabase(payload) {
    console.log("📊 ADD PAYA RECORD TO DATABASE:", payload);

    if (process.env.NODE_ENV === "development") {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return {
        success: true,
        message: "Added to fraud database",
        fraudData: {
          orderNumber: payload.orderNumber,
          email: payload.email,
          reviewedBy: "admin",
        },
      };
    }

    try {
      const response = await this.post(
        `/api/admin/paya/add-to-database`,
        payload
      );
      return response;
    } catch (err) {
      console.error("❌ Error adding Paya record to database:", err);
      throw err;
    }
  }

  /**
   * Fetch fraud patterns
   * @param {boolean} force - Whether to force fetch from API
   * @returns {Promise<Object>} The fraud patterns data
   */
  async fetchFraudPatterns(force = false) {
    return this.get("/api/admin/fraud-patterns", { force });
  }

  /**
   * Clear a fraud block
   * @param {Object} blockData - Block data to clear
   * @returns {Promise<Object>} The response data
   */
  // Enhanced clearBlock method for EnhancedApiClient.js
  async clearBlock(blockData) {
    try {
      // Validate input data
      if (!blockData || !blockData.blockType) {
        console.error("Invalid block data provided");
        return {
          status: "error",
          message: "Invalid block data provided",
        };
      }

      // Ensure blockType is normalized to lowercase
      let normalizedBlockType = String(blockData.blockType).toLowerCase();

      // Skip "pending" blocks - they can't be cleared
      if (normalizedBlockType === "pending") {
        return {
          status: "error",
          message: "Cannot clear pending blocks - they are not yet blocked",
        };
      }

      // Normalize blockType values more robustly
      if (normalizedBlockType.includes("cookie")) {
        normalizedBlockType = "cookie";
      } else if (normalizedBlockType.includes("ip")) {
        normalizedBlockType = "ip";
      } else if (normalizedBlockType.includes("email")) {
        normalizedBlockType = "email";
      } else {
        // If we have an unknown block type, return an error
        return {
          status: "error",
          message: `Unknown block type: ${normalizedBlockType}`,
        };
      }

      // Create a clean data object to send to the API
      const apiData = {
        blockType: normalizedBlockType,
        clearCounters: true, // IMPORTANT: Add this flag to also clear counters
      };

      // Add the appropriate identifier based on block type
      if (normalizedBlockType === "cookie" && (blockData.symphonyCookie || blockData.identifier)) {
        apiData.symphonyCookie = blockData.symphonyCookie || blockData.identifier;
        apiData.identifier = blockData.symphonyCookie || blockData.identifier;
      } else if (normalizedBlockType === "ip" && blockData.ipAddress) {
        apiData.ipAddress = blockData.ipAddress;
      } else if (normalizedBlockType === "email" && blockData.email) {
        apiData.email = blockData.email;
      } else {
        // If we have no appropriate identifier, return an error
        return {
          status: "error",
          message: `Missing identifier for ${normalizedBlockType} block type`,
        };
      }

      // In development mode, track the cleared block and return mock success response
      if (process.env.NODE_ENV === "development") {
        // Store this block as cleared to filter it from future responses
        if (typeof clearedBlocks !== "undefined") {
          clearedBlocks.push(apiData);
        }

        return {
          status: "success",
          message: `Block ${apiData.blockType} cleared successfully`,
        };
      }

      // For production/staging, call the API with properly formatted data
      const response = await this.post("/api/admin/clear-block", apiData, {
        timeout: 10000, // 10 second timeout for this operation
      });

      return response;
    } catch (error) {
      console.error("Error in clearBlock method:", error);

      // Return structured error
      return {
        status: "error",
        message: error.message || "Failed to clear block",
        details: error.response?.data || {},
      };
    }
  }

  /**
   * Clear all fraud blocks
   * @returns {Promise<Object>} Result with count of cleared blocks
   */
  async clearAllBlocks() {
    try {
      // In development mode, clear all tracked blocks and return mock success response
      if (process.env.NODE_ENV === "development") {
        // Get the number of blocks that would be cleared
        const mockData = getMockCardDeclines();
        const blockCount = mockData.declines.length;

        // Clear all blocks by clearing the clearedBlocks array and
        // then adding all current blocks to it
        if (typeof clearedBlocks !== "undefined") {
          // Store all blocks as cleared
          mockData.declines.forEach((decline) => {
            let blockType = "";
            let blockIdentifier = {};

            if (decline.blockType.toString().toUpperCase().includes("IP")) {
              blockType = "ip";
              blockIdentifier = { ipAddress: decline.ipAddress };
            } else if (
              decline.blockType.toString().toUpperCase().includes("EMAIL")
            ) {
              blockType = "email";
              blockIdentifier = { email: decline.email };
            }

            clearedBlocks.push({
              blockType,
              ...blockIdentifier,
            });
          });
        }

        return {
          status: "success",
          message: `All blocks cleared successfully (${blockCount} total)`,
          count: blockCount,
        };
      }

      // For production, call the API with an increased timeout
      const response = await this.post(
        "/api/admin/clear-all-blocks",
        {},
        {
          timeout: 30000, // Increase timeout to 30 seconds
        }
      );
      return response;
    } catch (error) {
      console.error("Error in clearAllBlocks method:", error);

      // Return structured error
      return {
        status: "error",
        message: error.message || "Failed to clear all blocks",
        details: error.response?.data || {},
      };
    }
  }

  /**
   * Clear all fraud warnings data
   * This is a special method to completely remove all fraud warnings
   * @returns {Promise<Object>} Result of the operation
   */
  async clearAllFraudWarnings() {
    try {
      // STEP 1: Clear client-side caches

      // Clear specific endpoint cache
      this.clearCacheForEndpoint("/api/admin/fraud-warnings");

      // Clear ALL localStorage items related to warnings or USER_CONTEXTS
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.includes("warning") ||
            key.includes("Warning") ||
            key.includes("fraud") ||
            key.includes("Fraud") ||
            key.includes("USER_CONTEXTS") ||
            key.includes("dashboard_fraud"))
        ) {
          localStorage.removeItem(key);
        }
      }

      // Override any timestamps
      localStorage.setItem("dashboard_fraud-warnings_timestamp", Date.now());

      // Approach 1: Use the nuke-all-kvs endpoint with specific params
      try {
        const nukeResponse = await this.post("/api/admin/nuke-all-kvs", {
          target: "warnings",
          scope: "limited",
          forceBypass: true,
          timestamp: Date.now(),
        });
        console.log("Nuke response:", nukeResponse);
      } catch (nukeError) {
        console.warn("Nuke approach failed:", nukeError.message);
      }

      // Approach 2: Use the direct-kv-test endpoint
      try {
        const directKvResponse = await this.post("/api/admin/direct-kv-test", {
          action: "clear_warnings",
          timestamp: Date.now(),
        });
        console.log("Direct KV test response:", directKvResponse);
      } catch (directError) {
        console.warn("Direct KV approach failed:", directError.message);
      }

      // Approach 3: Use the debug-kv-keys endpoint to identify keys
      try {
        const keysResponse = await this.get("/api/admin/debug-kv-keys", {
          params: { prefix: "warning" },
          force: true,
          timestamp: Date.now(),
        });
        console.log("Debug KV keys response:", keysResponse);
      } catch (keysError) {
        console.warn("Debug KV keys approach failed:", keysError.message);
      }

      // Approach 4: Overwrite the warnings data with empty data
      try {
        await this.post("/api/admin/test-address-mismatch", {
          sessionId: "clear_all_warnings",
          timestamp: Date.now(),
          forceEmpty: true,
          billing_info: {},
          shipping_info: {},
        });
      } catch (overwriteError) {
        console.warn("Overwrite approach failed:", overwriteError.message);
      }

      // Use a unique timestamp to force a completely fresh fetch
      const uniqueTimestamp = Date.now();

      // Create options and actually USE them with the get method
      const options = {
        force: true,
        skipCache: true,
        timestamp: uniqueTimestamp,
        noCache: true,
        bypassCache: true,
      };

      // Try the get method with our cache-busting options
      try {
        const warningsResponse = await this.get(
          "/api/admin/fraud-warnings",
          options
        );
        console.log("Fresh warnings response via get:", warningsResponse);
      } catch (getError) {
        console.warn("Error using get method:", getError.message);

        // Fall back to direct fetch as a backup
        try {
          // Try a completely fresh fetch with headers to bypass cache
          const freshResponse = await fetch(
            `${this.baseURL}/api/admin/fraud-warnings?t=${uniqueTimestamp}`,
            {
              method: "GET",
              headers: {
                "X-API-Key": this.authService.getApiKey() || "",
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
                "X-Force-Fresh": "true",
              },
              cache: "no-store",
            }
          );

          let responseData = { warnings: [] };
          try {
            responseData = await freshResponse.json();
            console.log("Fresh warnings response via fetch:", responseData);
          } catch (e) {
            console.warn("Error parsing fresh response:", e);
          }

          // If we still have warnings, try one more radical approach
          if (responseData.warnings && responseData.warnings.length > 0) {
            // Last resort: Use the nuclear option
            try {
              const nuclearResponse = await this.post(
                "/api/admin/nuke-all-kvs",
                {
                  force: true,
                  confirm: "yes",
                  timestamp: Date.now(),
                }
              );
              console.log("Nuclear option response:", nuclearResponse);
            } catch (nuclearError) {}
          }
        } catch (fetchError) {}
      }

      return {
        status: "success",
        message: "Fraud warnings cleared using multiple approaches",
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error("Error clearing fraud warnings:", error);
      return {
        status: "error",
        message: error.message || "Unknown error clearing fraud warnings",
        timestamp: new Date().toISOString(),
      };
    }
  }
  /**
   * Get shadow mode status (REAL DATA ONLY)
   * @returns {Promise<Object>} The shadow mode status
   */
  async getShadowModeStatus() {
    try {
      // ALWAYS bypass cache and make real API call
      const response = await this.request(
        "GET",
        "/api/admin/shadow-mode-status",
        {
          skipCache: true, // Force skip cache
          timeout: 5000, // 5 second timeout
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
            "X-No-Cache": Date.now().toString(), // Cache buster
          },
        }
      );

      // Never store shadow mode in cache
      this.clearCacheForEndpoint("/api/admin/shadow-mode-status");

      return response;
    } catch (err) {
      console.error("❌ Error fetching real shadow mode status:", err);
      throw new Error(`Failed to fetch shadow mode status: ${err.message}`);
    }
  }

  /**
   * Get config information
   * @returns {Promise<Object>} The config data
   */
  async getConfig() {
    if (process.env.NODE_ENV === "development") {
      return getMockConfig();
    }
    return this.get("/api/config");
  }
}

// Create a singleton instance
const apiClient = new EnhancedApiClient();
export default apiClient;
